import {
  nookieSetCookie,
  nookieDestroyCookie,
  nookieParseCookies,
} from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import {
  previewAudiencesCookieName,
  previewTestDateCookieName,
  previewShowAllCookieName,
} from './consts';
import { clientCookieOptions } from '../../utils/clientCookieOptions';

export type PreviewSettings = {
  audiences?: string;
  testDate?: string;
  showAll?: string;
};

const COOKIE_OPTIONS = { ...clientCookieOptions, maxAge: 86400 };

const readCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  return nookieParseCookies(undefined)[name];
};

const writeCookie = (name: string, value: string) => {
  nookieSetCookie(undefined, name, value, COOKIE_OPTIONS);
};

const deleteCookie = (name: string) => {
  nookieDestroyCookie(undefined, name, { path: '/' });
};

const loadFromCookies = (): PreviewSettings => {
  if (typeof document === 'undefined') return {};
  return {
    audiences: readCookie(previewAudiencesCookieName),
    testDate: readCookie(previewTestDateCookieName),
    showAll: readCookie(previewShowAllCookieName),
  };
};

let settings: PreviewSettings = loadFromCookies();
const listeners = new Set<() => void>();

const EMPTY_SETTINGS: PreviewSettings = {};
/** SSR-safe server snapshot for useSyncExternalStore — must return the same reference every call. */
export const previewSettingsServerSnapshot = (): PreviewSettings => EMPTY_SETTINGS;

const COOKIE_MAP: Record<keyof PreviewSettings, string> = {
  audiences: previewAudiencesCookieName,
  testDate: previewTestDateCookieName,
  showAll: previewShowAllCookieName,
};

/** All URL/query param keys managed by the preview settings store. Derived from PreviewSettings so it stays in sync with the type. */
export const previewSettingsParamKeys = Object.keys(COOKIE_MAP) as Array<keyof PreviewSettings>;

export const previewSettingsStore = {
  get: (): PreviewSettings => settings,
  // set() merges — present keys with undefined values delete the corresponding cookie,
  // absent keys are left untouched.
  set: (update: Partial<PreviewSettings>) => {
    const next: PreviewSettings = { ...settings };
    for (const key of Object.keys(update) as Array<keyof PreviewSettings>) {
      const value = update[key];
      next[key] = value;
      if (typeof document !== 'undefined') {
        if (value === undefined) {
          deleteCookie(COOKIE_MAP[key]);
        } else {
          writeCookie(COOKIE_MAP[key], value);
        }
      }
    }
    settings = next;
    listeners.forEach((l) => l());
  },
  clear: () => {
    settings = {};
    if (typeof document !== 'undefined') {
      deleteCookie(previewAudiencesCookieName);
      deleteCookie(previewTestDateCookieName);
      deleteCookie(previewShowAllCookieName);
    }
    listeners.forEach((l) => l());
  },
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
